import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesTwoToneIcon from '@mui/icons-material/TipsAndUpdatesTwoTone';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form3468 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 3468: Investment Credit for Form 990-T | TaxZerone"
          description="Learn how to attach IRS Form 3468 to Form 990-T to claim investment credits. Explore eligibility, filing steps, and tips to maximize your tax benefits."
          keywords=""
          canonical="resources/tax-exempt/form-3468-investment-credit/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form{' '}
          <span className="highlight-container">
            <span className="highlight"> 3468</span>
          </span>{' '}
          Investment Credit
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-3468-investment-credit/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 3468 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form 3468? Form 3468, Investment Credit,
                        </strong>{' '}
                        is used to calculate and claim eligible{' '}
                        <strong style={{ fontWeight: 600 }}>
                          investment credits{' '}
                        </strong>
                        for qualifying property, facilities, and energy-related
                        investments.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Uses It?
                        </strong>{' '}
                        Taxpayers and certain{' '}
                        <strong style={{ fontWeight: 600 }}>
                          tax-exempt or governmental entities{' '}
                        </strong>{' '}
                        may use Form 3468 when they qualify for an applicable
                        investment credit or make an eligible elective payment
                        election.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Form 3468 reports information about{' '}
                        <strong style={{ fontWeight: 600 }}>
                          qualified property or facilities,
                        </strong>
                        applicable investment credit calculations, and credit
                        amounts under provisions such as the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          energy credit, rehabilitation credit, clean
                          electricity investment credit, and advanced
                          manufacturing investment credit.
                        </strong>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Form 990-T Connection:{' '}
                        </strong>
                        Certain tax-exempt organizations making an{' '}
                        <strong style={{ fontWeight: 600 }}>
                          elective payment election{' '}
                        </strong>{' '}
                        for eligible investment credits must{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-3468-investment-credit/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          file Form 3468
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-3800-business-credits/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 3800
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}>with </strong>{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-T
                        </Link>{' '}
                        or another applicable income tax return.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  // id="what"
                >
                  What’s New in Form 3468
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The latest updates to Form 3468 mainly introduce new
                  compliance steps and some changes to how certain credits work.
                  A key update is the addition of{' '}
                  <span style={{ fontWeight: 600 }}> Form 7220,</span> which is
                  now required if you’re claiming a higher credit amount under
                  Sections 48, 48C, or 48E by meeting prevailing wage and
                  apprenticeship (PWA) rules. You’ll need to file this form
                  separately for each project.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  There are also small updates to{' '}
                  <span style={{ fontWeight: 600 }}>Part I,</span> including new
                  fields like a DOE control number. On the credit side, the{' '}
                  <span style={{ fontWeight: 600 }}>
                    advanced manufacturing credit (Part IV) now allows up to 35%
                    for certain investments,
                  </span>{' '}
                  while the{' '}
                  <span style={{ fontWeight: 600 }}>
                    clean electricity (Part V)
                  </span>{' '}
                  and
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    energy credit (Part VI)
                  </span>{' '}
                  have added restrictions, especially related to foreign
                  entities and some energy properties. In addition, the{' '}
                  <span style={{ fontWeight: 600 }}>
                    transition rule for the rehabilitation credit (Part VII)
                  </span>{' '}
                  has been removed.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 3468?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 3468?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to file Form 3468
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What is Form 3468?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 3468 is used to claim a credit for each investment
                  property and any unused investment credit amount from
                  cooperatives. The purpose of the form is to help taxpayers
                  offset the cost of qualified investments through tax credits,
                  thereby reducing their overall tax liability.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  These credits can be claimed for various investments, such as
                  energy property, new equipment, and qualified rehabilitation
                  expenditures. They not only reduce tax liability but also
                  encourage businesses and organizations to invest in
                  sustainable and economically positive initiatives.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who must file Form 3468?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Generally, any organization that is subject to UBIT and has
                  claimed investment credits must file Form 3468. This includes
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Businesses and Organizations:
                    </strong>{' '}
                    Entities that invest in qualifying rehabilitation, renewable
                    energy, or advanced energy projects.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Tax-Exempt Organizations with UBIT:{' '}
                    </strong>
                    Tax-exempt organizations filing Form 990-T can file Form
                    3468 if they have unrelated business income and meet the
                    requirements for investment credits.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Partnerships and Corporations:
                    </strong>{' '}
                    Partnerships, S-corporations, and corporations investing in
                    qualifying projects must file Form 3468 to claim their
                    investment credits.
                  </li>
                </ul>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Choose TaxZerone to complete your Form 4562 filing
                        requirements.
                      </Typography>
                      <Typography variant="body2">
                        Your journey to staying tax-compliant starts here!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 130, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form3468`}
                      >
                        Get Started
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                  <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="howto"
                >
                  How to file Form 3468
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-invest-img.webp" />
                  <source srcSet="/assets/images/f3468-invest-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-invest-img.png"
                    loading="lazy"
                    alt="Form 3468-Investment Credit an attachment in Form 990-T"
                    width={760}
                    height={122}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When completing Form 3468, the top section requires basic
                  identification details to link the form with your main tax
                  return.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Name(s) shown on return
                    </strong>{' '}
                    <br /> Enter the exact name as it appears on your primary
                    tax return. This could be your individual name, business
                    name, or organization name. The name must match your main
                    filing to ensure proper processing.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Identifying number
                    </strong>{' '}
                    <br /> Enter your tax identification number such as an{' '}
                    <span style={{ fontWeight: 600 }}> SSN</span> for
                    individuals or an{' '}
                    <span style={{ fontWeight: 600 }}>EIN</span> for businesses
                    and tax-exempt organizations. This number is used by the IRS
                    to correctly associate the form with your account.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part I-Information on Qualified Property or Qualified Facility
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-part1-img.webp" />
                  <source srcSet="/assets/images/f3468-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-part1-img.png"
                    loading="lazy"
                    alt="Form 3468 Part I-Information on Qualified Property or Qualified Facility Line1-Line5"
                    width={760}
                    height={341}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      IRS-issued registration number
                    </span>{' '}
                    for the facility or property received through the pre-filing
                    registration process, required under Sections 48D, 6417, or
                    6418.{' '}
                  </li>
                  <li>
                    For Part IV, enter the registration number of the{' '}
                    <span style={{ fontWeight: 600 }}>
                      facility if you are the owner
                    </span>
                    , or the{' '}
                    <span style={{ fontWeight: 600 }}>
                      qualified investment if you are not the owner.
                    </span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2a(i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>emissions rate</span> of the
                  facility (kg of CO ‚‚e per kg of hydrogen produced).{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2a(ii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>DOE control number</span>{' '}
                  received from the Department of Energy
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2b(i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if you receive an emissions value from the
                  Department of Energy (DOE).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2b(ii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if you used an{' '}
                  <span style={{ fontWeight: 600 }}>
                    IRS-approved Lifecycle Analysis (LCA) model
                  </span>{' '}
                  to determine the emissions value.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2b(iii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}> DOE control number,</span>{' '}
                  to support your emissions data.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3a{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the type of facility or property for which you are
                    claiming the credit (for example, solar, clean hydrogen, or
                    rehabilitation).
                  </li>
                  <li>
                    If you received unused credits from a cooperative, enter:
                    “Unused Investment Credit from Cooperatives.”
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3b{' '}
                </Typography>
                <Typography variant="body1">
                  If the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    owner of the facility is different from the filer,
                  </span>
                  provide the following details:
                </Typography>
                <Box
                  component="ol"
                  sx={{
                    mb: 2,
                    '& li': { mb: 1.2, listStyleType: 'lower-roman' },
                  }}
                >
                  <li>Enter Owner’s name</li>
                  <li>Enter Owner’s taxpayer identification number (TIN)</li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the address of the facility or property, if applicable.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide the{' '}
                  <span style={{ fontWeight: 600 }}>location coordinates</span>{' '}
                  of the facility ie., Latitude and Longitude.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if the project includes{' '}
                  <span style={{ fontWeight: 600 }}>
                    qualified interconnection property
                  </span>{' '}
                  under Sections 48(a)(8) or 48E(b)(1)(B).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date when construction of the facility or property
                  started. This helps determine eligibility for certain credits.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date when the facility or property was ready and
                  available for use. This is important for calculating and
                  claiming the credit.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-part1-line6-11-img.webp" />
                  <source srcSet="/assets/images/f3468-part1-line6-11-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-part1-line6-11-img.png"
                    loading="lazy"
                    alt="Form 3468 Part I-Information on Qualified Property or Qualified Facility Line6-Line11"
                    width={760}
                    height={507}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Indicate whether the facility is an expansion of an existing
                    facility.
                  </li>
                  <li>
                    Select “Yes” if the project adds to or upgrades an existing
                    setup; otherwise, select “No.”
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7
                </Typography>
                <Typography variant="body1">
                  Indicate whether the facility{' '}
                  <span style={{ fontWeight: 600 }}>produces electricity.</span>
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If “Yes” Select if the facility generates electricity.
                  </li>
                  <li>
                    If “No” Select if the facility does not generate
                    electricity.
                  </li>
                  <li>
                    If “not applicable” Select if this question does not apply
                    to your facility or project.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate whether your project meets the prevailing wage and
                  apprenticeship requirements, which determine eligibility for
                  the increased credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8a
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    In Special allocation cases select “Yes” if PWA requirements
                    are met and specific rules under sections 48C(e)(5) and (6)
                    apply, including allocations declared under IRS guidance
                    (such as Notice 2023-18).
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8b
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 2,
                    '& li': { mb: 1.2 },
                    '& ol li': { listStyleType: 'lower-roman' },
                  }}
                >
                  <li>
                    For General compliance select “Yes” if PWA requirements are
                    met under general rules, either because:
                  </li>
                  <ol>
                    <li>If construction began before January 29, 2023, or</li>
                    <li>
                      The project complies with applicable PWA provisions under
                      Sections 48(a)(10) and (11), or 48E(d)(3) and (4).{' '}
                    </li>
                  </ol>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “No” if the project does not meet PWA requirements
                  (only the base credit may apply).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8d
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select “Not applicable” if PWA requirements do not apply to
                  your project.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Remember
                  that meeting the criteria outlined by PWA is essential to
                  ensure that you qualify for the total amount of credit
                  increase. If you fail to meet these criteria, the credit will
                  only be available at its basic level.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section helps you establish whether your project
                  qualifies for an increased credit amount or adjustment through
                  particular eligibility criteria.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your project complies with the eligibility criteria under
                  sections 48 and 48E and qualifies for the bonus credit of 10%,
                  check here.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select this if your project qualifies for a reduced 2% bonus
                  credit but does not fully meet the required conditions.
                  Supporting documents must still be attached.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select "No” if your project does not qualify for any bonus
                  credit under these provisions.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate whether your project qualifies for any additional
                  bonus credit based on specific eligibility criteria
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this if your project meets the requirements under
                  Section 48(a)(9)(B), Section 48E(a)(2)(A)(ii), or Section
                  48E(a)(2)(B)(ii) to qualify for the full 10% bonus credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this if your project qualifies under the same
                  sections—Section 48(a)(9)(B), Section 48E(a)(2)(A)(ii), or
                  Section 48E(a)(2)(B)(ii)—but does not fully meet the
                  conditions, resulting in a reduced 2% bonus credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select this if your project does not meet the requirements
                  under these sections and is not eligible for any bonus credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Indicate whether the property, facility, or project
                    qualifies for the low-income communities bonus credit under
                    Section 48(e)(2) or Section 48E(h)(2).
                  </li>
                  <li>
                    This applies only if the project has received an official
                    capacity limitation allocation.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select this if the facility is located in a low-income
                  community as defined under Section 45D(e). This qualifies for
                  a 10% bonus credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select this if the facility is located on Indian land, as
                  defined under Section 2601(2) of Public Law 102-486. This also
                  qualifies for a 10% bonus credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select this if the facility is part of a qualified low-income
                  residential building project under Section 48(e)(2)(B) or
                  Section 48E(h)(2)(B). This qualifies for a 20% bonus credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select this if the facility is part of a qualified low-income
                  economic benefit project under Section 48(e)(2)(C) or Section
                  48E(h)(2)(C). This qualifies for a 20% bonus credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you selected “Yes” for any of the above (11a–11d), enter
                  your Section 48(e) or 48E(h) control number received for the
                  allocation.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    EIN of the originating pass-through entity,
                  </span>{' '}
                  if the credit is being claimed through another entity
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select this if the project does not qualify for the low-income
                  community's bonus credit under these sections.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-part1-line12-13-img.webp" />
                  <source srcSet="/assets/images/f3468-part1-line12-13-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-part1-line12-13-img.png"
                    loading="lazy"
                    alt="Form 3468 Part I-Information on Qualified Property or Qualified Facility Line12-Line13"
                    width={760}
                    height={400}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide the{' '}
                  <span style={{ fontWeight: 600 }}> capacity details</span> of
                  your facility, depending on the type of property.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12a(i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    nameplate capacity in kilowatts (kW)
                  </span>{' '}
                  measured in
                  <span style={{ fontWeight: 600 }}> direct current (DC)</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12a(ii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    nameplate capacity in kilowatts (kW)
                  </span>{' '}
                  measured in{' '}
                  <span style={{ fontWeight: 600 }}>
                    alternating current (AC)
                  </span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12a(iii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the box if the solar property includes a{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    solar tracking device
                  </span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the nameplate capacity in kilowatts (kW) for the wind
                  facility typically measured in AC.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12c(i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Specify the type of property (if it is not solar or
                  wind).{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12c(ii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    nameplate capacity in kilowatts (kW)
                  </span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12c(iii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check whether the capacity is measured in{' '}
                  <span style={{ fontWeight: 600 }}>AC or DC</span> by selecting
                  the appropriate option.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12d(i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    power capacity rating in kilowatts (kW)
                  </span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12d(ii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    energy storage capacity
                  </span>{' '}
                  in kilowatt-hours (kWh){' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12d(iii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check whether the storage system is installed{' '}
                  <span style={{ fontWeight: 600 }}>
                    with a solar or wind facility as thermal storage
                  </span>{' '}
                  by selecting “Yes” or “No.”
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Indicate whether you are{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    claiming the investment credit as a lessee
                  </span>{' '}
                  under Section 48(d).{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Select “Yes” if the lessor has passed the credit to you,
                    then complete Lines 13a-13e. If you have multiple leased
                    properties, provide details separately for each.{' '}
                  </li>
                  <li>Select “No” if not applicable. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the full name of the lessor (owner of the property).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the lessor’s business address.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide a short description of the leased property.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount considered as the purchase value for
                  calculating the credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13e
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the income the lessor must report related to passing the
                  credit to you.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part II-Qualifying Advanced Coal Project Credit and Qualifying
                  Gasification Project Credit
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Section A-Qualifying Advanced Coal Project Credit Under
                  Section 48A
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partII-seca-img.webp" />
                  <source srcSet="/assets/images/f3468-partII-seca-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partII-seca-img.png"
                    loading="lazy"
                    alt="Form 3468 Part II Section A-Qualifying Advanced Coal Project Credit Under Section 48A "
                    width={760}
                    height={254}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    qualified investment amount
                  </span>{' '}
                  for projects using{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    integrated gasification combined cycle technology
                  </span>{' '}
                  under{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Section 48A(d)(3)(B)(i)
                  </span>
                  . This includes property used to convert coal into gas for
                  electricity generation.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the amount on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Line 1a by 20% (0.20)
                  </span>{' '}
                  to calculate the credit amount.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    qualified investment amount
                  </span>{' '}
                  for advanced coal-based generation projects under Section
                  48A(d)(3)(B)(ii) that do not use gasification technology.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate the credit amount by multiplying the figure from
                  Line 2a by 15 percent (0.15).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the investment amount that qualifies for projects
                  covered under Section 48A(d)(3)(B)(iii), which incorporates
                  carbon capture equipment to limit greenhouse gas emissions
                  (generally 65 percent or more).{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3b
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The credit amount is calculated by multiplying the figure from
                  Line 3a by 30 percent (0.30).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Section B-Qualifying Gasification Project Credit Under Section
                  48B
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partII-secb-img.webp" />
                  <source srcSet="/assets/images/f3468-partII-secb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partII-secb-img.png"
                    loading="lazy"
                    alt="Form 3468 Part II Section B-Qualifying Gasification Project Credit Under Section 48B "
                    width={760}
                    height={223}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4a
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the qualified investment amount in qualifying
                    gasification project property placed in service in the tax
                    year under Section 48B.{' '}
                  </li>
                  <li>
                    The property must be related to gasification technology
                    capable of separating and capturing no less than 75 percent
                    of carbon dioxide (CO2) emissions.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate the credit amount by multiplying the amount entered
                  in Line 4a by 30 percent (0.30) in relation to the qualifying
                  gasification project under Section 48B.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the qualified investment amount in qualifying
                  gasification project property placed in service during the tax
                  year, except for any amount included in Line 4a.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the amount on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Line 5a by 20% (0.20)
                  </span>{' '}
                  to calculate the credit amount.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    If you are a patron of a cooperative, enter any unused
                    investment credit allocated to you from a qualifying
                    advanced coal project or gasification project.
                  </li>
                  <li>
                    If you are a cooperative, follow the instructions under{' '}
                    <span style={{ fontWeight: 600 }}>Form 3800</span> to
                    allocate credits to your patrons.
                  </li>
                </Box>
                <Alert
                  severity="info"
                  sx={{ mb: 5, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesTwoToneIcon />}
                >
                  <strong style={{ fontWeight: 600 }}>Tip:</strong> Report any
                  unused credits passed to you through a cooperative.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Add the amounts from{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Lines 1b, 2b, 3b, 4b, 5b, and 6
                    </span>{' '}
                    to calculate your total investment credit for these
                    projects.{' '}
                  </li>
                  <li>
                    Report this total on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Form 3800, Part III, line 1a
                    </span>
                    .
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part III-Qualifying Advanced Energy Project Credit Under
                  Section 48C
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partIII-img.webp" />
                  <source srcSet="/assets/images/f3468-partIII-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partIII-img.png"
                    loading="lazy"
                    alt="Form 3468 Part III-Qualifying Advanced Energy Project Credit Under Section 48C"
                    width={760}
                    height={208}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    qualified investment amount
                  </span>{' '}
                  in a{' '}
                  <span style={{ fontWeight: 600 }}>
                    qualifying advanced energy project
                  </span>{' '}
                  under. This is the cost (basis) of eligible property placed in
                  service during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable credit rate:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> 30%</span> if you meet
                    the prevailing wage and apprenticeship (PWA) requirements
                    and the project was certified accordingly under
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Section 48C(e)
                    </span>{' '}
                    (per IRS guidance).
                  </li>
                  <li>
                    Otherwise, enter{' '}
                    <span style={{ fontWeight: 600 }}>6%.</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the{' '}
                  <span style={{ fontWeight: 600 }}>
                    qualified investment (Line 1a)
                  </span>{' '}
                  by the{' '}
                  <span style={{ fontWeight: 600 }}>
                    applicable percentage (Line 1b)
                  </span>{' '}
                  to calculate the{' '}
                  <span style={{ fontWeight: 600 }}>credit amount</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    Section 48C allocation control number
                  </span>{' '}
                  received for the qualifying project.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1e
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Indicate whether the facility is located in a{' '}
                  <span style={{ fontWeight: 600 }}>
                    Section 48C energy community census tract
                  </span>
                  .{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Select “Yes” if the project is located in a qualifying
                    energy community area.
                  </li>
                  <li>Select “No” if it is not. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the TIN reported in{' '}
                  <span style={{ fontWeight: 600 }}>Part I, Line 3b(ii)</span>{' '}
                  is different from the{' '}
                  <span style={{ fontWeight: 600 }}>
                    originating pass-through entity’s EIN,
                  </span>
                  enter that EIN. Otherwise, leave this line blank.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If you are a patron of a cooperative, enter any unused
                    investment credit from a qualifying advanced energy project
                    (Section 48C) that was allocated to you.
                  </li>
                  <li>
                    If you are a cooperative, follow Form 3800 instructions to
                    allocate credits to your patrons.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you are a{' '}
                  <span style={{ fontWeight: 600 }}>
                    partnership or S corporation
                  </span>{' '}
                  and choose to transfer the credit under Section 6418(c), enter
                  the total credit amount here.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part IV-Advanced Manufacturing Investment Credit Under Section
                  48D
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partIV-img.webp" />
                  <source srcSet="/assets/images/f3468-partIV-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partIV-img.png"
                    loading="lazy"
                    alt="Form 3468 Part IV-Advanced Manufacturing Investment Credit Under Section 48D "
                    width={760}
                    height={159}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Select the type of advanced manufacturing project you are
                  claiming the credit for:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Semiconductor manufacturing facility, or</li>
                  <li>Semiconductor equipment manufacturing facility </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total basis of your{' '}
                  <span style={{ fontWeight: 600 }}>qualified investmen</span>t
                  in the advanced manufacturing facility placed in service
                  during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Calculate the credit based on when the property was placed in
                  service:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>25% (0.25):</span> If
                    placed in service{' '}
                    <span style={{ fontWeight: 600 }}>before 2026</span>{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> 35% (0.35):</span> If
                    placed in service{' '}
                    <span style={{ fontWeight: 600 }}>after 2025</span>{' '}
                    (generally for fiscal-year filers ending in 2026){' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> The 25%
                  credit rate increases to 35% for property placed in service
                  after 2025. This mainly applies to fiscal-year filers whose
                  tax year ends in 2026.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If you are a patron of a cooperative, enter any unused
                    advanced manufacturing investment credit (Section 48D)
                    allocated to you.{' '}
                  </li>
                  <li>
                    If you are a cooperative, follow{' '}
                    <span style={{ fontWeight: 600 }}>
                      Form 3800, Part III, line 1o
                    </span>{' '}
                    to allocate credits to your patrons.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If you are a{' '}
                    <span style={{ fontWeight: 600 }}>
                      partnership or S corporation
                    </span>{' '}
                    electing to receive a{' '}
                    <span style={{ fontWeight: 600 }}>direct payment</span> of
                    the credit under
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Section 48D(d)(2)(A)
                    </span>
                    , enter the{' '}
                    <span style={{ fontWeight: 600 }}>credit amount</span>{' '}
                    here.{' '}
                  </li>
                  <li>
                    Also report this amount on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Form 3800, Part III, line 1o
                    </span>
                    .{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution: </strong> You
                  cannot claim the advanced manufacturing investment credit
                  (Section 48D) for any tax year if the construction of the
                  qualified property begins after 2026.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part V-Clean Electricity Investment Credit Under Section 48E
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section A-Qualified Clean Electricity Facilities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partv-seca-img.webp" />
                  <source srcSet="/assets/images/f3468-partv-seca-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partv-seca-img.png"
                    loading="lazy"
                    alt="Form 3468 Part V Section A-Qualified Clean Electricity Facilities"
                    width={760}
                    height={576}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total investment in a qualified facility under
                  Section 48E(b)(1) that was placed in service during the tax
                  year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>credit percentage</span>{' '}
                  based on eligibility (see increased credit rules).{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    For{' '}
                    <span style={{ fontWeight: 600 }}>
                      qualified fuel cell property under Section 48(c)(1)
                    </span>
                    (used to generate electricity and with construction
                    beginning after 2026), enter{' '}
                    <span style={{ fontWeight: 600 }}>30%</span>.
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution: </strong> If you
                  are claiming credit for qualified fuel cell property, no
                  additional increases or adjustments are allowed. Skip Lines 1d
                  through 1n and go directly to Line 2.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 1a by Line 1b</span> to
                  calculate the credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1d
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      domestic content bonus percentage
                    </span>{' '}
                    if your project meets the requirements.
                  </li>
                  <li>
                    If not eligible, leave it blank and skip to{' '}
                    <span style={{ fontWeight: 600 }}>Line 1f.</span>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 1a by Line 1d</span> to
                  calculate the domestic content bonus credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1f
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      energy community bonus percentage
                    </span>{' '}
                    if the facility is located in a qualifying area.{' '}
                  </li>
                  <li>
                    If not applicable, leave it blank and skip to{' '}
                    <span style={{ fontWeight: 600 }}>Line 1h</span>.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 1a by Line 1f</span> to
                  calculate the energy community bonus credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1h
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    low-income communities bonus percentage
                  </span>{' '}
                  if your project qualifies.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  You cannot{' '}
                  <span style={{ fontWeight: 600 }}>claim this bonus</span>{' '}
                  if:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    You selected “No” in{' '}
                    <span style={{ fontWeight: 600 }}>Part I, Line 11g,</span>{' '}
                    or
                  </li>
                  <li>
                    Your project capacity is{' '}
                    <span style={{ fontWeight: 600 }}>5 MW AC or more</span>
                    .{' '}
                  </li>
                  <li>
                    {' '}
                    If not eligible, enter{' '}
                    <span style={{ fontWeight: 600 }}>
                      0 on Lines 1h and 1n,
                    </span>{' '}
                    then go to <span style={{ fontWeight: 600 }}>Line 2</span>
                    .{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1i
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>capacity (in kW)</span> that
                  was officially allocated to your project in the allocation
                  letter.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1j
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If your project’s total capacity (from{' '}
                    <span style={{ fontWeight: 600 }}>Part I, Line 12</span>) is
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      equal to the allocated capacity (Line 1i)
                    </span>{' '}
                  </li>
                  <li>
                    Multiply{' '}
                    <span style={{ fontWeight: 600 }}>Line 1a by Line 1h</span>{' '}
                    and go directly to{' '}
                    <span style={{ fontWeight: 600 }}>Line 1n</span>.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1k
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If your project capacity is greater than the allocated
                    capacity{' '}
                  </li>
                  <li>
                    Divide Line 1i by the total project capacity (Part I, Line
                    12) to get the eligible proportion.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1l
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the{' '}
                  <span style={{ fontWeight: 600 }}>
                    low-income bonus percentage (Line 1h)
                  </span>{' '}
                  by the{' '}
                  <span style={{ fontWeight: 600 }}>
                    proportion from Line 1k
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1m
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 1a by Line 1l</span> to
                  calculate the{' '}
                  <span style={{ fontWeight: 600 }}>
                    adjusted bonus credit amount
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1n
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If your project capacity is{' '}
                    <span style={{ fontWeight: 600 }}>
                      greater than the allocated capacity,
                    </span>{' '}
                    enter the amount from Line 1m.
                  </li>
                  <li>
                    Otherwise, enter the amount from{' '}
                    <span style={{ fontWeight: 600 }}>Line 1j</span>.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Lines 1c, 1e, 1g, and 1n
                  </span>{' '}
                  to calculate the{' '}
                  <span style={{ fontWeight: 600 }}>
                    total credit amount
                  </span>{' '}
                  for this section.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section B-Qualified Energy Storage Technology
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partv-secb-img.webp" />
                  <source srcSet="/assets/images/f3468-partv-secb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partv-secb-img.png"
                    loading="lazy"
                    alt="Form 3468 Part V Section B-Qualified Energy Storage Technology"
                    width={760}
                    height={382}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total investment (basis) in energy storage
                  technology under Section 48E(c) that was placed in service
                  during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>credit percentage</span>{' '}
                  based on eligibility .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 3a by Line 3b</span> to
                  calculate the{' '}
                  <span style={{ fontWeight: 600 }}>base credit amount</span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3d
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      domestic content bonus percentage
                    </span>{' '}
                    if the energy storage project meets the requirements.
                  </li>
                  <li>
                    If not eligible, leave blank, skip{' '}
                    <span style={{ fontWeight: 600 }}>Line 3e,</span> and go to{' '}
                    <span style={{ fontWeight: 600 }}>Line 3f</span>.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 3a by Line 3d</span> to
                  calculate the domestic content bonus credit.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3f
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the energy community bonus percentage if the project
                    is located in a qualifying area.{' '}
                  </li>
                  <li>
                    {' '}
                    If not applicable, leave blank, skip{' '}
                    <span style={{ fontWeight: 600 }}>Line 3g,</span> and go to
                    <span style={{ fontWeight: 600 }}> Line 4</span>.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 3a by Line 3f</span> to
                  calculate the energy community bonus credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Lines 3h – 3n
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These lines are{' '}
                  <span style={{ fontWeight: 600 }}>
                    reserved for future use.
                  </span>{' '}
                  No entry is required for these lines.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Lines 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from{' '}
                  <span style={{ fontWeight: 600 }}> Lines 3c, 3e, and 3g</span>{' '}
                  to calculate the total credit for energy storage technology.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section C-Totals, Credit Reduction for Subsidized Energy
                  Financing or Private Activity Bonds, and Credit Phaseout
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partv-secc-img.webp" />
                  <source srcSet="/assets/images/f3468-partv-secc-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partv-secc-img.png"
                    loading="lazy"
                    alt=" Form 3468 Part V Section C-Totals, Credit Reduction for Subsidized Energy Financing"
                    width={760}
                    height={544}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Lines 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts from{' '}
                  <span style={{ fontWeight: 600 }}>Part V, Lines 2 and 4</span>{' '}
                  to get the total clean electricity and energy storage
                  credit.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6a
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Divide:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Total subsidized financing or bond proceeds used (current +
                    prior years) by, Total capital investment in the project
                    (current + prior years)
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 5 by Line 6a</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 5 by 15% (0.15)</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller,{' '}
                  <span style={{ fontWeight: 600 }}>Line 6b or Line 6c</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract{' '}
                  <span style={{ fontWeight: 600 }}>Line 6d from Line 5.</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    If your project used{' '}
                    <span style={{ fontWeight: 600 }}>
                      subsidized energy financing or private activity bonds,
                    </span>{' '}
                    enter the amount from{' '}
                    <span style={{ fontWeight: 600 }}>Line 6e</span>.{' '}
                  </li>
                  <li>
                    Otherwise, enter the amount from{' '}
                    <span style={{ fontWeight: 600 }}>Line 5.</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line applies if you are choosing an{' '}
                  <span style={{ fontWeight: 600 }}>
                    elective payment (direct payment)
                  </span>{' '}
                  under Section 6417 and your project{' '}
                  <span style={{ fontWeight: 600 }}>
                    does not meet certain requirements
                  </span>{' '}
                  (like domestic content or size limits).{' '}
                </Typography>
                <Typography variant="body1">
                  If applicable, reduce your credit (Line 7) using:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>90%</span> if construction
                    began in <span style={{ fontWeight: 600 }}>2024</span>{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>85%</span> if construction
                    began in <span style={{ fontWeight: 600 }}>2025</span>{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>0%</span> if construction
                    began{' '}
                    <span style={{ fontWeight: 600 }}>after 2025</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If not applicable, simply{' '}
                  <span style={{ fontWeight: 600 }}>
                    enter the amount from Line 7
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is{' '}
                  <span style={{ fontWeight: 600 }}>
                    reserved for future use
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter any{' '}
                    <span style={{ fontWeight: 600 }}>
                      unused clean electricity investment credit
                    </span>
                    (Section 48E) received from a cooperative.{' '}
                  </li>
                  <li>
                    If you are a cooperative, follow{' '}
                    <span style={{ fontWeight: 600 }}>
                      Form 3800, Part III, line 1v
                    </span>{' '}
                    to allocate credits to your patrons.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Add <span style={{ fontWeight: 600 }}>Lines 8 and 10</span>{' '}
                    to calculate the total credit amount.{' '}
                  </li>
                  <li>
                    Report this amount on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 3800, Part III, line 1v
                    </span>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part VI-Energy Credit Under Section 48
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section A-Geothermal Energy Credit
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section A-Geothermal Energy Credit"
                    width={760}
                    height={244}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total basis of{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    geothermal energy property
                  </span>{' '}
                  placed in service during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}> credit percentage</span>{' '}
                  based on:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>When construction began, and</li>
                  <li>
                    Whether PWA (prevailing wage & apprenticeship) requirements
                    are met{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}> Line 1a by Line 1b</span>{' '}
                  to calculate the base credit amount.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    applicable domestic content bonus percentage
                  </span>{' '}
                  for your project{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}> Line 1a by Line 1d</span>{' '}
                  to calculate the domestic content bonus credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1f
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      applicable energy community bonus percentage
                    </span>{' '}
                    (see energy community bonus rules).{' '}
                  </li>
                  <li>
                    If the project is not located in a qualifying energy
                    community, leave this line blank, skip Line 1g, and go to
                    <span style={{ fontWeight: 600 }}> Line 2.</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 1a by Line 1f to calculate the energy community
                  bonus credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from Lines 1c, 1e, and 1g to calculate the
                  total geothermal energy credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section B-Solar Energy Credit
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-secb-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-secb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-secb-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section B-Solar Energy Credit "
                    width={760}
                    height={598}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3a
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the total basis of{' '}
                    <span style={{ fontWeight: 600 }}>
                      solar energy property
                    </span>{' '}
                    placed in service during the tax year.{' '}
                  </li>
                  <li>
                    Includes solar equipment, solar facilities, and
                    electrochromic glass.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3b
                </Typography>
                <Typography variant="body1">
                  Enter the credit percentage based on:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>When construction began, and </li>
                  <li>
                    Whether{' '}
                    <span style={{ fontWeight: 600 }}>PWA requirements</span>{' '}
                    are met{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 3a by Line 3b</span> to
                  calculate the base credit.{' '}
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution: </strong> If the
                  property falls under{' '}
                  <span style={{ fontWeight: 600 }}>Section 48(a)(3)(ii)</span>,
                  it does not qualify for low-income bonus. Skip
                  <span style={{ fontWeight: 600 }}> Lines 3d–3j</span> and go
                  to <span style={{ fontWeight: 600 }}>Line 3k</span>.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3d
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the applicable low-income communities bonus percentage
                  for your solar project.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  However, you do not qualify for this bonus if:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    You selected “No” in{' '}
                    <span style={{ fontWeight: 600 }}>Part I, Line 11g,</span>{' '}
                    or
                  </li>
                  <li>
                    Your project capacity is{' '}
                    <span style={{ fontWeight: 600 }}>5 MW AC or more</span>{' '}
                    (based on Part I, Line 12a(ii)).{' '}
                  </li>
                  <li>
                    If not eligible, enter{' '}
                    <span style={{ fontWeight: 600 }}>
                      0% on Lines 3d and 3j,
                    </span>{' '}
                    and continue to{' '}
                    <span style={{ fontWeight: 600 }}>Line 3k.</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    allocated capacity (kW DC)
                  </span>{' '}
                  from your allocation letter.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your{' '}
                  <span style={{ fontWeight: 600 }}>
                    project capacity = allocated capacity,
                  </span>{' '}
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 3a by Line 3d</span>{' '}
                  and go to <span style={{ fontWeight: 600 }}>Line 3j</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your{' '}
                  <span style={{ fontWeight: 600 }}>
                    project capacity is higher,
                  </span>{' '}
                  Divide{' '}
                  <span style={{ fontWeight: 600 }}>
                    allocated capacity (3e)
                  </span>{' '}
                  by <span style={{ fontWeight: 600 }}>total capacity</span>{' '}
                  (Part I, Line 12a(i)).{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3h
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 3d by Line 3g</span> to
                  adjust the bonus percentage.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3i
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 3a by Line 3h</span> to
                  calculate the{' '}
                  <span style={{ fontWeight: 600 }}>adjusted bonus credit</span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3j
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If capacity exceeds allocation on Line 3e, enter the amount
                    in <span style={{ fontWeight: 600 }}>Line 3i.</span>
                  </li>
                  <li>
                    Otherwise, enter{' '}
                    <span style={{ fontWeight: 600 }}>Line 3f</span>.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3k
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      applicable domestic content bonus percentage
                    </span>{' '}
                    for your solar project.{' '}
                  </li>
                  <li>
                    If the project does not meet the requirements, leave this
                    line blank, skip{' '}
                    <span style={{ fontWeight: 600 }}>Line 3l,</span> and
                    continue to <span style={{ fontWeight: 600 }}>Line 3m</span>
                    .{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3l
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 3a by Line 3k</span> to
                  calculate the{' '}
                  <span style={{ fontWeight: 600 }}>
                    domestic content bonus credit
                  </span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3m
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the applicable energy community bonus percentage.
                  </li>
                  <li>
                    If the project is not located in a qualifying energy
                    community, leave this line blank, skip{' '}
                    <span style={{ fontWeight: 600 }}>Line 3n,</span> and go to
                    <span style={{ fontWeight: 600 }}>Line 4</span>.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3n
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 3a by Line 3m</span> to
                  calculate the energy community bonus credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Lines 3c, 3j, 3l, and 3n
                  </span>{' '}
                  to calculate the total solar energy credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section C-Qualified Fuel Cell Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-secc-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-secc-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-secc-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section C-Qualified Fuel Cell Property "
                    width={760}
                    height={539}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the basis of qualified fuel cell property tied to
                  periods after 2005 and before October 4, 2008, placed in
                  service during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 5a by 30% (0.30)</span>{' '}
                  to calculate the initial credit amount based on your
                  investment.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    total capacity (in kilowatts)
                  </span>{' '}
                  related to the amount in Line 5a. Must be a whole number.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 5c by $1,000</span> to
                  determine the maximum credit allowed based on capacity.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller{' '}
                  <span style={{ fontWeight: 600 }}>Line 5b or Line 5d</span>.
                  This ensures your credit does not exceed the{' '}
                  <span style={{ fontWeight: 600 }}>per-kilowatt limit</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5f
                </Typography>
                <Typography variant="body1">
                  Enter the basis of qualified fuel cell property tied to
                  periods{' '}
                  <span style={{ fontWeight: 600 }}>
                    after October 3, 2008,
                  </span>{' '}
                  where:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Construction began{' '}
                    <span style={{ fontWeight: 600 }}>
                      before 2021 or after 2022,
                    </span>{' '}
                    and
                  </li>
                  <li>
                    The property was placed in service during the tax year.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    applicable energy credit percentage
                  </span>{' '}
                  based on eligibility.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5h
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 5f by Line 5g</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5i
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      applicable domestic content bonus percentage
                    </span>{' '}
                    for the project.{' '}
                  </li>
                  <li>
                    If the project does not meet the requirements, leave this
                    line blank, skip{' '}
                    <span style={{ fontWeight: 600 }}>Line 5j,</span> and
                    continue to <span style={{ fontWeight: 600 }}>Line 5l</span>
                    .
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5j
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 5f by Line 5i</span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5k
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is{' '}
                  <span style={{ fontWeight: 600 }}>
                    reserved for future use
                  </span>
                  . No entry is required.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5l
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      applicable energy community bonus percentage
                    </span>{' '}
                    for the project.
                  </li>
                  <li>
                    If the project is not located in a qualifying energy
                    community, leave this line blank, skip{' '}
                    <span style={{ fontWeight: 600 }}>Line 5m,</span> and go to
                    <span style={{ fontWeight: 600 }}>Line 5n</span>.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5m
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 5f by Line 5l</span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5n
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}>Lines 5h, 5j, and 5m</span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5o – Capacity (kW)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total kilowatt capacity related to the investment
                  reported on <span style={{ fontWeight: 600 }}>Line 5f</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5p
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 5o by $3,000</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5q
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller of{' '}
                  <span style={{ fontWeight: 600 }}>Line 5n or Line 5p</span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add <span style={{ fontWeight: 600 }}> Lines 5e and 5q</span>{' '}
                  to calculate the total fuel cell credit.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section D-Qualified Microturbine Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-secd-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-secd-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-secd-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section D-Qualified Microturbine Property "
                    width={761}
                    height={368}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total basis of{' '}
                  <span style={{ fontWeight: 600 }}>
                    qualified microturbine property
                  </span>{' '}
                  placed in service during the tax year. Include amounts related
                  to periods after 2005.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable energy credit percentage based on
                  eligibility.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 7a by Line 7b</span> to
                  calculate the base credit amount.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7d
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the applicable domestic content bonus percentage for
                    the project.
                  </li>
                  <li>
                    If the project does not meet the requirements, leave this
                    line blank, skip{' '}
                    <span style={{ fontWeight: 600 }}>Line 7e</span>, and
                    continue to <span style={{ fontWeight: 600 }}>Line 7g</span>
                    .{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 7a by Line 7d</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is reserved for future use.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7g
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the applicable energy community bonus percentage.{' '}
                  </li>
                  <li>
                    If the project is not located in a qualifying energy
                    community, leave this line{' '}
                    <span style={{ fontWeight: 600 }}>blank,</span> skip{' '}
                    <span style={{ fontWeight: 600 }}>Line 7h,</span> and go to
                    <span style={{ fontWeight: 600 }}>Line 7i</span>.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7h
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>
                    Line 7a by Line 7g
                  </span>{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7i
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}>Lines 7c, 7e, and 7h</span>{' '}
                  to determine the total microturbine credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7j
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total kilowatt capacity related to the investment
                  reported on <span style={{ fontWeight: 600 }}>Line 7a</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7k
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is reserved for future use.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7l
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply{' '}
                  <span style={{ fontWeight: 600 }}>Line 7j by $200</span> to
                  calculate the maximum credit allowed based on capacity.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the smaller of{' '}
                  <span style={{ fontWeight: 600 }}> Line 7i or Line 7l</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section E-Combined Heat and Power System Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-sece-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-sece-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-sece-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section E-Combined Heat and Power System Property "
                    width={760}
                    height={379}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the basis of property using Combined heat and power
                  (CHP) system placed in service during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9b
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Calculate the capacity adjustment factor:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    If electrical capacity is in MW: divide 15 by MW capacity
                    (enter 1.0 if ≤ 15 MW)
                  </li>
                  <li>
                    If capacity is in horsepower: divide 20,000 by horsepower
                    (enter 1.0 if ≤ 20,000 hp){' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 9a by Line 9b.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable energy percentage for the project.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 9c by Line 9d.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable domestic content bonus credit percentage.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 9c by Line 9f to calculate the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    domestic content bonus credit
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9h
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable energy community bonus credit percentage.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9i
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 9c by Line 9h .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines 9e, 9g, and 9i to determine the total Combined Heat
                  and Power (CHP) system credit.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section F-Qualified Small Wind Energy Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-secf-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-secf-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-secf-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section F-Qualified Small Wind Energy Property"
                    width={760}
                    height={556}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11a-11c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is reserved for future use.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11d
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the basis of qualified small wind energy property placed
                  in service during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  This applies only if:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Acquired the property, or</li>
                  <li>Constructed, reconstructed, or erected it yourself.</li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable energy percentage for the project.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 11d by Line 11e
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable low-income communities bonus credit
                  percentage for the small wind energy facility.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11h
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the capacity limitation allocated to you as stated in
                  the allocation letter.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11i
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If Part I, Line 12b equals Line 11h, then Multiply Line 11d
                    by Line 11g, this becomes your bonus credit amount. Then
                    skip to Line 11m.{' '}
                  </li>
                  <li>If not equal, proceed to Line 11j. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11j
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If Part I, Line 12b is greater than Line 11h, calculate:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Divide Line 11h by Part I, Line 12b. This gives a proportional
                  allocation factor.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11k
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 11g by Line 11j.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11l
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 11d by Line 11k.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11m
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Final determination of the bonus credit:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If Part I, Line 12b is greater than Line 11h, enter the
                    amount from Line 11l
                  </li>
                  <li>Otherwise, enter the amount from Line 11i. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11n
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable domestic content bonus credit percentage.
                  If the project does not meet domestic content requirements,
                  then:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Leave Line 11n blank </li>
                  <li>Skip Line 11o </li>
                  <li>Proceed directly to Line 11p </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11n
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 11d by Line 11n{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11p
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable energy community bonus credit percentage.
                  If the project is not located in a qualifying energy
                  community, then:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Leave Line 11p blank</li>
                  <li>Skip Line 11q </li>
                  <li>Proceed to Line 12 </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11q
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 11d by Line 11p.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines 11f, 11m, 11o, and 11q to determine the total credit
                  for the small wind energy property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section G-Waste Energy Recovery Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-secg-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-secg-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-secg-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section G-Waste Energy Recovery Property "
                    width={761}
                    height={260}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the basis of waste energy recovery property placed in
                  service during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable energy percentage for the project.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 13a by Line 13b.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13d
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable domestic content bonus credit percentage.
                  If the project does not meet the domestic content
                  requirements, then:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Leave Line 13d blank</li>
                  <li>Skip Line 13e </li>
                  <li>Proceed to Line 13f </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 13a by Line 13d.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13f
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable energy community bonus credit percentage.
                  If the project is not located in a qualifying energy
                  community, then:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Leave Line 13f blank </li>
                  <li>Skip Line 13g </li>
                  <li>Proceed to Line 14 </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 13a by Line 13f.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines 13c, 13e, and 13g to determine the total waste
                  energy recovery property credit.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section H-Geothermal Heat Pump Systems
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-sech-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-sech-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-sech-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section H-Geothermal Heat Pump Systems "
                    width={761}
                    height={239}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the basis of property using geothermal heat pump systems
                  placed in service during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable energy percentage for the project.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 15a by Line 15b to calculate the base credit
                  amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15d
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable domestic content bonus credit percentage.
                  If the project does not meet domestic content requirements,
                  then:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Leave Line 15d blank</li>
                  <li>Skip Line 15e </li>
                  <li>Proceed to Line 15f </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 15a by Line 15d to calculate the domestic
                  content bonus credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15f
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable energy community bonus credit percentage.
                  If the project is not located in a qualifying energy
                  community, then:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Leave Line 15f blank </li>
                  <li>Skip Line 15g </li>
                  <li>Proceed to Line 16 </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 15a by Line 15f to calculate the energy
                  community bonus credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines 15c, 15e, and 15g to determine the total geothermal
                  heat pump system credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section I-Energy Storage Technology Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-seci-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-seci-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-seci-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section I-Energy Storage Technology Property"
                    width={761}
                    height={689}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the basis of energy storage technology property placed
                  in service during the tax year. This includes only the portion
                  of cost attributable to construction, reconstruction, or
                  erection by the taxpayer.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable energy percentage.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 17a by Line 17b to calculate the base energy
                  storage credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17d
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable low-income communities bonus credit
                  percentage
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  However, you are not eligible for this bonus if either of the
                  following applies:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>You checked the box in Part I, Line 11g, or </li>
                  <li>Part I, Line 12a(ii) or 12b is 5 MW AC or more </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  If either condition applies:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Enter 0 on Lines 17d and 17j </li>
                  <li>Skip to Line 17k </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the capacity limitation allocated to you for the solar
                  or wind energy property associated with the energy storage
                  system.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17f
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the value in Part I, Line 12a(i) or 12b equals Line 17e,
                    then: Multiply Line 17a by Line 17d, Enter the result here,
                    then skip to Line 17j{' '}
                  </li>
                  <li>If not equal, continue to Line 17g. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Part I, Line 12a(i) or 12b is greater than Line 17e,
                  calculate: Line 17e divided by Part I, Line 12a(i) or 12b
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17h
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 17d by Line 17g to calculate the adjusted bonus
                  percentage.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17i
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 17a by Line 17h to calculate the adjusted
                  low-income communities bonus credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17i
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Final determination:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If Part I, Line 12a(i) or 12b is greater than Line 17e,
                    enter the amount from Line 17i
                  </li>
                  <li>Otherwise, enter the amount from Line 17f </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17k
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable domestic content bonus credit percentage.
                  If the project does not meet domestic content requirements,
                  then:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Leave Line 17k blank </li>
                  <li>Skip Line 17l </li>
                  <li>Proceed to Line 17m </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17l
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 17a by Line 17k to calculate the domestic
                  content bonus credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17m
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable energy community bonus credit percentage.
                  If the project is not located in a qualifying energy
                  community, then:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Leave Line 17m blank </li>
                  <li>Skip Line 17n </li>
                  <li>Proceed to Line 18 </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17n
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 17a by Line 17m.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines 17c, 17j, 17l, and 17n to determine the total energy
                  storage technology credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section J-Qualified Biogas Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-secj-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-secj-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-secj-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section J-Qualified Biogas Property "
                    width={761}
                    height={243}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 19a
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the basis of qualified biogas energy property placed
                    in service during the tax year.
                  </li>
                  <li>
                    Include only the portion of the cost attributable to
                    construction, reconstruction, or erection by the
                    taxpayer.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 19b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable energy percentage.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 19c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 19a by Line 19b to calculate the base biogas
                  energy credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 19d
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the applicable domestic content bonus credit percentage.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the project does not meet domestic content requirements,
                  then: leave Line 19d blank, skip Line 19e and proceed to Line
                  19f .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 19e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 19a by Line 19d.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 19f
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the applicable energy community bonus credit percentage.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the project is not placed in service within an energy
                  community, then: leave Line 19f blank, skip Line 19g and
                  proceed to Line 20.
                </Typography>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 19g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 19a by Line 19f.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 20
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines 19c, 19e, and 19g to determine the total qualified
                  biogas energy property credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section K-Microgrid Controllers Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-seck-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-seck-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-seck-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section K-Microgrid Controllers Property "
                    width={761}
                    height={256}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 21a
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the basis of qualified microgrid controller property
                    placed in service during the tax year.
                  </li>
                  <li>
                    Include the portion of the cost attributable to
                    construction, reconstruction, or erection by the taxpayer.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 21b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable energy percentage.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 21c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 21a by Line 21b to calculate the base microgrid
                  controller credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 21d
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the applicable domestic content bonus credit
                    percentage.
                  </li>
                  <li>
                    If the project does not meet domestic content requirements,
                    then: leave Line 21d blank, skip Line 21e and proceed to
                    Line 21f.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 21e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 21a by Line 21d.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 21f
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the applicable energy community bonus credit
                    percentage.
                  </li>
                  <li>
                    {' '}
                    If the project is not located in a qualifying energy
                    community, then: leave Line 21f blank, skip Line 21g and
                    proceed to Line 22{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 21g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 21a by Line 21f to calculate the energy
                  community bonus credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 22
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines 21c, 21e, and 21g to determine the total qualified
                  microgrid controller property credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section L-Qualified Investment Credit Facility Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-secl-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-secl-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-secl-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section L-Qualified Investment Credit Facility Property "
                    width={761}
                    height={570}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the basis of investment credit facility property placed
                  in service during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23b
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable base credit percentage:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter 30% if you checked the box in Part I, Line 7a or 8b
                  </li>
                  <li>Otherwise, enter 6%</li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 23a by Line 23b.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution:</strong> If the
                  property is not described under section 45(d)(1), it does not
                  qualify for the low-income community bonus credit for wind
                  facilities. In that case, Skip Lines 23d through 23j and
                  proceed directly to Line 23k
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23d
                </Typography>
                <Typography variant="body1">
                  Enter the applicable low-income communities bonus credit
                  percentage for the wind energy facility.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    You do not qualify for this bonus if you checked the box in
                    Part I, Line 11g, or if Part I, Line 12b is 5 MW AC or more
                  </li>
                  <li>
                    If either condition applies, enter 0 on Lines 23d and 23j
                  </li>
                  <li>
                    Skip the remaining steps in this section and proceed to Line
                    23k.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23e
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the capacity limitation allocated to you as per the
                  allocation letter.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note</strong> Solar
                  capacity is reported in DC, and Wind capacity may be reported
                  in AC or DC.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23f
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If Part I, Line 12b equals Line 23e, then: Multiply Line 23a
                    by Line 23d, enter the result here, then skip to Line 23j.
                  </li>
                  <li> If not equal, continue to Line 23g.</li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Part I, Line 12b is greater than Line 23e, divide Line 23e
                  by Part I, Line 12b.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23h
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 23d by Line 23g to calculate the adjusted
                  low-income bonus percentage.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23i
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 23a by Line 23h to calculate the adjusted
                  low-income communities bonus credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23j
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Determine the final low-income bonus credit amount:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If Part I, Line 12b is greater than Line 23e, enter the
                    amount from Line 23i{' '}
                  </li>
                  <li>Otherwise, enter the amount from Line 23f. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23k
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable domestic content bonus credit percentage.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the project does not meet the requirements, leave Line
                    23k blank
                  </li>
                  <li>Skip Line 23l and proceed to Line 23m </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23l
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 23a by Line 23k.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23m
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable energy community bonus credit percentage.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the project is not located in a qualifying energy
                    community, leave Line 23m blank
                  </li>
                  <li>Skip Line 23n and proceed to Line 24 </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 23n
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 23a by Line 23m.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line24
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines 23c, 23j, 23l, and 23n to determine the total
                  investment credit facility property credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section M-Clean Hydrogen Production Facilities as Energy
                  Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-secm-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-secm-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-secm-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section M-Clean Hydrogen Production Facilities as Energy Property "
                    width={761}
                    height={505}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25a
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the basis of property placed in service during the tax
                    year for a facility designed to produce qualified clean
                    hydrogen.
                  </li>
                  <li>
                    The process must result in a lifecycle greenhouse gas
                    emission rate of no more than 4 kg of CO ‚‚e per kg of
                    hydrogen, and not less than 2.5 kg.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25b
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable energy percentage:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Enter 6% if you checked the box in Part I, Line 8b</li>
                  <li>Enter 1.2% if you checked the box in Part I, Line 8c</li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 25a by Line 25b.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25d
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the basis of property for a facility designed to
                    produce qualified clean hydrogen with a lower emission
                    range.
                  </li>
                  <li>
                    The process must result in emissions less than 2.5 kg of
                    CO ‚‚e per kg of hydrogen, and not less than 1.5 kg.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25e
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable energy percentage:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Enter 6% if you checked the box in Part I, Line 8b</li>
                  <li>Enter 1.2% if you checked the box in Part I, Line 8c </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 25d by Line 25e.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25g
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the basis of property placed in service during the tax
                    year for a facility designed to produce qualified clean
                    hydrogen.
                  </li>
                  <li>
                    The process must result in emissions less than 1.5 kg of
                    CO ‚‚e per kg of hydrogen, but not less than 0.45 kg.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25h
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  {' '}
                  Enter the applicable energy percentage:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Enter 10% if you checked the box in Part I, Line 8b </li>
                  <li>Enter 2% if you checked the box in Part I, Line 8c</li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25i
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 25g by Line 25h.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25j
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  {' '}
                  Enter the basis of property for a facility designed to produce
                  qualified clean hydrogen with the lowest emission range.{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The process must result in emissions less than 0.45 kg of
                    CO ‚‚e per kg of hydrogen{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25k
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  {' '}
                  Enter the applicable energy percentage:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter <span style={{ fontWeight: 600 }}>30%</span> if you
                    checked the box in Part I, Line 8b{' '}
                  </li>
                  <li>
                    Enter <span style={{ fontWeight: 600 }}>6%</span> if you
                    checked the box in Part I, Line 8c{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25l
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 25j by Line 25k to calculate the credit amount
                  for this category.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 26
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines{' '}
                  <span style={{ fontWeight: 600 }}>
                    25c, 25f, 25i, and 25l
                  </span>{' '}
                  to determine the total clean hydrogen property credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Section N-Totals and Credit Reduction for Tax-Exempt Bonds
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partvi-secn-img.webp" />
                  <source srcSet="/assets/images/f3468-partvi-secn-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partvi-secn-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VI Section N-Totals and Credit Reduction for Tax-Exempt Bonds "
                    width={761}
                    height={386}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>{' '}
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 27
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Part VI Lines 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24,
                  and 26 to calculate the total energy credit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  If tax-exempt bond proceeds were not used to finance the
                  facility:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Skip Line 28</li>
                  <li>Proceed directly to Line 29</li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28a
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Calculate the tax-exempt bond financing ratio:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Divide the total{' '}
                    <span style={{ fontWeight: 600 }}>
                      tax-exempt bond proceeds
                    </span>{' '}
                    used (current + prior years){' '}
                  </li>
                  <li>
                    By the total{' '}
                    <span style={{ fontWeight: 600 }}>capital additions</span>{' '}
                    to the facility (current + prior years){' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 27 by Line 28a to determine the credit reduction
                  based on bond financing.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 27 by{' '}
                  <span style={{ fontWeight: 600 }}>15% (0.15)</span>.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller{' '}
                  <span style={{ fontWeight: 600 }}>
                    of Line 28b or Line 28c
                  </span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 28e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract Line 28d from Line 27 to determine the adjusted total
                  energy credit after limitation.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 29
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the appropriate credit amount:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If tax-exempt bond proceeds were used, enter the amount from
                    Line 28e
                  </li>
                  <li>Otherwise, enter the amount from Line 27</li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 30
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Determine if a reduction applies under section 6417:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If you are making an elective payment election for a
                    facility whose construction began in 2024, and it does not
                    meet certain requirements, multiply Line 29 by 90% (0.90)
                  </li>
                  <li>
                    Otherwise, enter the amount from Line 29 without any
                    adjustment .{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 31
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    unused investment credit from cooperatives
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 32
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines <span style={{ fontWeight: 600 }}> 30 and 31</span>{' '}
                  to calculate the{' '}
                  <span style={{ fontWeight: 600 }}> final credit amount</span>.
                  Report this amount on Form 3800, Part III, Line 4a.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part VII-Rehabilitation Credit Under Section 47
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3468-partviI-img.webp" />
                  <source srcSet="/assets/images/f3468-partviI-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3468-partviI-img.png"
                    loading="lazy"
                    alt="Form 3468 Part VII-Rehabilitation Credit Under Section 47"
                    width={720}
                    height={570}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check whether a{' '}
                  <span style={{ fontWeight: 600 }}>
                    charitable conservation contribution deduction
                  </span>{' '}
                  under section 170(h) was claimed for the property. This
                  applies only if you are claiming a credit for a{' '}
                  <span style={{ fontWeight: 600 }}>
                    certified historic structure
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  If you selected “Yes” on Line 1a, enter the{' '}
                  <span style={{ fontWeight: 600 }}>NPS project number</span>.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    This number is assigned by the National Park Service (NPS)
                    to:{' '}
                  </li>
                  <ul>
                    <li>A certified historic structure </li>
                    <li>
                      A building individually listed in the National Register of
                      Historic Places{' '}
                    </li>
                    <li>
                      A building located within a qualified historic
                      district{' '}
                    </li>
                  </ul>
                  <li>
                    If the property is a single building individually listed,
                    enter <span style={{ fontWeight: 600 }}>“00000”</span>{' '}
                  </li>
                  <li>For more details, refer to: </li>
                  <ul>
                    <li>
                      Instructions for Form 8283 (Noncash Charitable
                      Contributions)
                    </li>
                    <li>Publication 526 (Charitable Contributions) </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Indicate how you want to{' '}
                  <span style={{ fontWeight: 600 }}>
                    claim rehabilitation expenditures
                  </span>{' '}
                  for credit purposes.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Generally, expenses are claimed in the year the building is
                    placed in service
                  </li>
                  <li>
                    However, you may elect to claim them in the year they are
                    paid or incurred if:
                  </li>
                  <ul>
                    <li>The rehabilitation period is at least 2 years, and </li>
                    <li>
                      It is reasonable to expect the building will qualify when
                      placed in service{' '}
                    </li>
                  </ul>
                  <li>To make this election, check the box on Line 1c. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    24-month or 60-month measuring period
                  </span>{' '}
                  for the rehabilitation project. Provide both the{' '}
                  <span style={{ fontWeight: 600 }}>beginning date</span> and
                  <span style={{ fontWeight: 600 }}>ending date</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the adjusted basis of the building as of the beginning
                  date entered on Line 1d. If your holding period begins later,
                  use the first day of your holding period instead.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the qualified rehabilitation expenditures (QREs)
                  incurred or treated as incurred during the period specified on
                  Line 1d.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total qualified rehabilitation expenditures.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1h-1i
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is reserved for future use.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1j
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate the rehabilitation credit amount for qualified
                  expenditures paid or incurred after 2017. The total credit is
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    20% of qualified rehabilitation expenditures,
                  </span>{' '}
                  claimed over <span style={{ fontWeight: 600 }}>5 years</span>
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> The amount
                  reported on Line 1j represents the 4% credit allowed annually
                  over 5 years. Do not enter the full 20% credit amount on this
                  line.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1k
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide additional details if claiming a credit for a
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    certified historic structure:
                  </span>{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1k(i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the NPS project number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1k(ii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the EIN of the pass-through entity{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1k(iii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date of final certification of completed work
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1l
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line is reserved for future use.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1m
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  If{' '}
                  <span style={{ fontWeight: 600 }}>
                    final certification of completed work has not been received
                  </span>{' '}
                  at the time of filing:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the date that is{' '}
                    <span style={{ fontWeight: 600 }}>
                      30 months after the original credit claim date
                    </span>{' '}
                  </li>
                  <li>Attach: </li>
                  <ul>
                    <li>First page of NPS Form 10-168 (Part 2) </li>
                    <li>
                      Proof that the building is a certified historic structure
                      (or that status was requested){' '}
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  After receiving final certification:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>File Form 3468 with the next tax return </li>
                  <li>Enter the NPS project number and certification date </li>
                  <li>
                    Attach an explanation and include prior claimed credit
                    amounts{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  If certification is{' '}
                  <span style={{ fontWeight: 600 }}>
                    not received within 30 months:
                  </span>
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Submit a written statement to the IRS before the{' '}
                    <span style={{ fontWeight: 600 }}>30-month deadline</span>
                  </li>
                  <li>
                    You may need to agree to extend the assessment period under
                    section 6501(c)(4)
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution: </strong> If
                  certification is ultimately denied, the credit is disallowed,
                  and an amended return may be required.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>
                    unused rehabilitation investment credit
                  </span>{' '}
                  received from cooperatives.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Add Lines <span style={{ fontWeight: 600 }}>1j and 2</span>{' '}
                    to calculate the total rehabilitation credit.
                  </li>
                  <li>
                    Report this amount on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 3800, Part III, Line 4k
                    </span>
                  </li>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on the
                        official guidance and instructions published by the
                        Internal Revenue Service (IRS).
                        <ul>
                          <li style={{ marginBottom: 8 }}>
                            Form 3468{' '}
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/i3468.pdf"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Instructions
                            </Link>{' '}
                            (Investment Credit)
                          </li>
                          <li style={{ marginBottom: 8 }}>
                            Form 3800{' '}
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/i3800.pdf"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              instruction
                            </Link>{' '}
                            – General Business Credit{' '}
                          </li>
                          <li>
                            {' '}
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/i3800.pdf"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Prevailing Wage and Apprenticeship Requirements
                            </Link>{' '}
                          </li>
                        </ul>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 87,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What types of investments qualify for credit on Form 3468?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                      Form 3468 includes credits for rehabilitation, renewable
                      energy investments, advanced energy projects, and much
                      more.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. When is the deadline for filing Form 3468?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                      The deadline for filing Form 3468 is typically the same as
                      the{' '}
                      <Link
                        href="/resources/tax-exempt/form-990-due-dates/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        deadline for filing Form 990-T
                      </Link>
                      .
                    </Typography>
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{ borderLeft: '3px solid rgb(102, 60, 0)' }}
                    >
                      <strong style={{ fontWeight: 600 }}>Note:</strong> The
                      deadline is extended to the next business day if the due
                      date falls on a weekend or a legal holiday.
                    </Alert>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Do I need to file additional forms for each credit within
                  Form 3468?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography component="div" sx={{ mt: 1 }}>
                      For most credits, Form 3468 covers all necessary
                      information. However, some credits may require supporting
                      documentation, such as certifications for historic
                      rehabilitation projects.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form3468;
